import React, {useEffect} from 'react';
import {useField} from 'formik';
import Redactor from '../../assets/libs/redactor.usm.min';
import {variablePlugin} from '../../utils/redactor/variable';

type Props = {
  value?: string
  name: string
  id: string
  minHeight?: number

  /**
   * If true, the toolbar is visible only on code selection
   */
  air?: boolean

  /**
   * List of formatting styles
   */
  formatting?: string[]

  /**
   * List of tags to include in the wysiwyg
   */
  tags?: [string, string][]

  buttons?: WysiwigButtons
};

export default function UIWysiwig({
  id,
  air,
  formatting = ['p', 'blockquote', 'pre', 'h1', 'h2', 'h3', 'h4'],
  tags = [],
  minHeight,
  buttons,
  ...props
}: Props) {
  const [field, meta, helpers] = useField(props);
  const editorButtons = buttons || WysiwigButtons.default();
  const plugins = tags && tags.length ? ['variable'] : [];
  const variables = tags && tags.length ? tags : [];

  useEffect(() => {
    variablePlugin(Redactor);
    Redactor(`#${id}`, {
      maxHeight: '500px',
      source: true,
      air,
      formatting,
      plugins,
      minHeight: minHeight ? `${minHeight}px` : false,
      variables,
      buttons: editorButtons.get(),
      toolbarExternal: `#${id}_external_toolbar`,
      callbacks: {
        blur: () => {
          helpers.setTouched(true);
        },
        syncing: (html: string) => {
          helpers.setValue(html);
          return html;
        },
      },
    });

  });

  return (
    <>
      <div className="w-full flex flex-col redactor-rewrite-styles">
        <div className="bg-gray-light p-3 rounded-t-md border-gray border border-b-0" id={`${id}_external_toolbar`}/>
        <textarea id={id} {...field} />
        {meta.error && meta.touched &&
        <div className="text-xs text-red mt-1 ml-2">{meta.error}</div>
        }
      </div>
    </>
  );
}

export class WysiwigButtons {
  static default(): WysiwigButtons {
    return new WysiwigButtons().format().bold().italic()
      .strikethrough()
      .lists()
      .link();
  }

  protected buttons: string[];

  constructor() {
    this.buttons = [];
  }

  get() {
    return this.buttons;
  }

  format() {
    return this.add('format');
  }

  bold() {
    return this.add('bold');
  }

  italic() {
    return this.add('italic');
  }

  image() {
    return this.add('image');
  }

  link() {
    return this.add('link');
  }

  strikethrough() {
    return this.add('deleted');
  }

  lists() {
    return this.add('lists');
  }

  html() {
    return this.add('html');
  }

  protected add(type: string): WysiwigButtons {
    this.buttons.push(type);
    return this;
  }
}
